/**
 * Diversion Gauge Component
 * Displays real-time diversion progress with circular gauge visualization
 * Similar to ComplianceGauge but focused on job-specific diversion tracking
 */

import { formatPercentage, formatWeight } from '../../utils/formatting';
import type { JobDiversionStats } from '../../types/job';

interface DiversionGaugeProps {
  stats: JobDiversionStats;
}

export function DiversionGauge({ stats }: DiversionGaugeProps) {
  const {
    diversion_rate,
    target_diversion_rate,
    total_weight,
    diverted_weight,
    landfill_weight,
  } = stats;

  // Calculate circle dimensions
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset =
    circumference - (diversion_rate / 100) * circumference;

  const isCompliant = diversion_rate >= target_diversion_rate;
  const gap = Math.max(0, target_diversion_rate - diversion_rate);

  const gaugeColor = isCompliant ? 'text-accent-500' : 'text-orange-500';
  const statusColor = isCompliant ? 'text-accent-600' : 'text-orange-600';
  const statusBgColor = isCompliant ? 'bg-accent-100' : 'bg-orange-100';

  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-neutral-900 mb-6">
        Diversion Progress
      </h3>

      <div className="flex flex-col items-center">
        {/* Circular gauge */}
        <div className="relative w-48 h-48 mb-6">
          <svg
            className="transform -rotate-90 w-full h-full"
            viewBox="0 0 200 200"
          >
            {/* Background circle */}
            <circle
              cx="100"
              cy="100"
              r={radius}
              stroke="currentColor"
              strokeWidth="16"
              fill="none"
              className="text-neutral-200"
            />
            {/* Progress circle */}
            <circle
              cx="100"
              cy="100"
              r={radius}
              stroke="currentColor"
              strokeWidth="16"
              fill="none"
              className={gaugeColor}
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              style={{
                transition: 'stroke-dashoffset 0.5s ease-in-out',
              }}
            />
          </svg>

          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className={`text-4xl font-bold ${statusColor}`}>
              {formatPercentage(diversion_rate, 1)}
            </div>
            <div className="text-sm text-neutral-600 mt-1">Diverted</div>
          </div>
        </div>

        {/* Status badge */}
        <div
          className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${statusColor} ${statusBgColor} mb-4`}
        >
          {isCompliant ? (
            <>
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Meets Target ({target_diversion_rate}%)
            </>
          ) : (
            <>
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {gap.toFixed(1)}% to target
            </>
          )}
        </div>

        {/* Statistics */}
        <div className="w-full grid grid-cols-3 gap-4 text-center border-t border-neutral-200 pt-4">
          <div>
            <div className="text-sm text-neutral-600 mb-1">Total</div>
            <div className="text-lg font-semibold text-neutral-900">
              {formatWeight(total_weight)}
            </div>
          </div>
          <div>
            <div className="text-sm text-neutral-600 mb-1">Diverted</div>
            <div className="text-lg font-semibold text-accent-600">
              {formatWeight(diverted_weight)}
            </div>
          </div>
          <div>
            <div className="text-sm text-neutral-600 mb-1">Landfill</div>
            <div className="text-lg font-semibold text-red-600">
              {formatWeight(landfill_weight)}
            </div>
          </div>
        </div>

        {/* Progress indicator */}
        {!isCompliant && gap > 0 && (
          <div className="w-full mt-4 p-3 bg-orange-50 rounded-md">
            <p className="text-xs text-orange-700 text-center">
              Need {formatWeight((gap / 100) * total_weight)} more diversion to
              meet {target_diversion_rate}% target
            </p>
          </div>
        )}

        {isCompliant && (
          <div className="w-full mt-4 p-3 bg-accent-50 rounded-md">
            <p className="text-xs text-accent-700 text-center">
              Great job! You&apos;ve exceeded the {target_diversion_rate}% target
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
